import React, { useState, useCallback, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import styles from '../MagicSignTable.module.scss';
// Reuse Magic Upload's org-cell visuals (pill + search bar + icons) so the
// company selector looks and behaves exactly like Magic Upload's org column.
import muStyles from '../../../../MagicUpload/components/MagicUploadFilesTable/MagicUploadFilesTable.module.scss';
import CircularSearchBar from '../../../../MagicUpload/components/CircularSearchBar/CircularSearchBar';
import OpenMuOrgIcon from '../../../../../assets/Svg/OpenMUOrgIcon';
import CrossIcon from '../../../../../assets/Svg/CrossIcon';
import { getOrgList } from '../../../../MagicUpload/MagicUploadHelper';

interface OrgOption {
    orgName: string;
    assesseeOrgId: string;
}

interface OrgInlineSelectProps {
    rowId: string;
    pnoId: string;
    orgName?: string;
    /** Locks reassignment once the document is sent/signed/cancelled */
    locked?: boolean;
    onAssociate: (rowId: string, orgId: string) => void | Promise<void>;
}

/**
 * Searchable, changeable company selector for the global Magic Sign org column.
 * Mirrors Magic Upload's org-name cell — the same pill + CircularSearchBar + icons.
 * Editable: pill shows the company with a clear (X) icon that reveals the search.
 * Locked: the same pill without the X, in a muted colour to signal it can't change.
 */
const OrgInlineSelect: React.FC<OrgInlineSelectProps> = ({ rowId, pnoId, orgName, locked = false, onAssociate }) => {
    const [searching, setSearching] = useState(false);
    const [options, setOptions] = useState<OrgOption[]>([]);
    const [showList, setShowList] = useState(false);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clear the pending debounced search on unmount so a row closed mid-debounce can't fire
    // getOrgList against an unmounted component.
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const runSearch = useCallback(
        (text: string) => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            if (!pnoId || text.trim().length < 3) {
                setOptions([]);
                setShowList(false);
                return;
            }
            searchTimerRef.current = setTimeout(() => {
                getOrgList(
                    { pnoId, searchText: text },
                    {
                        onSuccess: (res: { orgName: string; orgId: string }[]) => {
                            setOptions((res ?? []).map((o) => ({ orgName: o.orgName, assesseeOrgId: o.orgId })));
                            setShowList(true);
                        },
                    },
                );
            }, 300);
        },
        [pnoId],
    );

    const handleSelect = useCallback(
        async (value: OrgOption): Promise<boolean> => {
            if (!value.assesseeOrgId) return false;
            try {
                await onAssociate(rowId, value.assesseeOrgId);
                return true;
            } catch (error) {
                console.error('[OrgInlineSelect] Failed to associate company', error);
                toast.error('Failed to associate company. Please try again.');
                return false;
            } finally {
                setSearching(false);
                setShowList(false);
            }
        },
        [rowId, onAssociate],
    );

    // Locked — same pill, no clear (X) icon, muted colour
    if (locked && orgName) {
        return (
            <div className={clsx(muStyles.OrgNamePill, styles.OrgPillLocked)} title={orgName}>
                {/* var(--magic-sign-locked-org-icon), not #9e9e9e — .OrgNamePill's filter:grayscale(1)
                 * renders a pale grey near-black, reading as a smaller icon rather than a muted one;
                 * muting is carried by .OrgPillLocked's text colour instead. */}
                <OpenMuOrgIcon colour="var(--magic-sign-locked-org-icon)" height="12" width="12" />
                {/* muStyles.OrgNameText, not a Magic Sign class — its min-width:0 pairs with the
                 * icon's `> svg { flex-shrink: 0 }` guard so the icon stays fixed and the name
                 * truncates instead. */}
                <span className={muStyles.OrgNameText}>{orgName}</span>
            </div>
        );
    }

    // Editable with a company — pill with the clear (X) icon that opens the search
    if (orgName && !searching) {
        return (
            <div className={clsx(muStyles.OrgNamePill, styles.OrgPill)} title={orgName}>
                <OpenMuOrgIcon colour="var(--primary-text-colour)" height="12" width="12" />
                <span className={muStyles.OrgNameText}>{orgName}</span>
                <span
                    className={muStyles.ClearOrgIcon}
                    title="Change company"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSearching(true);
                        setShowList(false);
                    }}
                >
                    <CrossIcon colour="var(--primary-text-colour)" width="9" height="9" />
                </span>
            </div>
        );
    }

    // Empty or actively changing — searchable company picker
    return (
        <div className={styles.OrgSearchWrap}>
            <CircularSearchBar
                placeholder="Search Name"
                options={options}
                showList={showList}
                onChange={runSearch}
                onSelect={handleSelect}
                onFocus={() => setShowList(false)}
                onOpenAddLead={() => {}}
            />
        </div>
    );
};

export default OrgInlineSelect;
